/**
 * Browser entry point for the WebVideoPlayer
 * Allows for the player to be installed in a browser environment using a script tag.
 */
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { merge } from 'lodash';
import { WebVideoPlayer } from './index';
import { VideoPlayerProps } from './typings';

// Store active instances to manage cleanup
const activeInstances = new Map<string, Root>();

const WebVideoPlayerDOM = {
  /**
   * Launch a WebVideoPlayer instance in the specified DOM container
   * @param options - Launch options containing selector, props, and optional configUrl
   * @returns Promise<string> - Returns an instance ID for cleanup
   */
  launch: async (options: {
    /** CSS selector for the target DOM element */
    selector: string;
    /** VideoPlayerProps to configure the player */
    props: VideoPlayerProps;
    /** Optional URL to fetch base configuration JSON */
    configUrl?: string;
  }): Promise<string> => {
    const { selector, props, configUrl } = options;
    const container = document.querySelector(selector);

    if (!container) {
      throw new Error(`DOM element with selector "${selector}" not found`);
    }

    let finalProps = props;

    // Fetch and merge config if URL provided
    if (configUrl) {
      try {
        const response = await fetch(configUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch config from ${configUrl}: ${response.status} ${response.statusText}`);
        }
        const baseConfig = await response.json();

        // Deep merge base config with props, with props taking precedence
        finalProps = merge({}, baseConfig, props);
      } catch (error) {
        console.error('Error fetching config from URL:', error);
        throw new Error(
          `Failed to load configuration from ${configUrl}: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    }

    // Generate unique instance ID
    const instanceId = `webvideoplayer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create React root and render the player
    const root = createRoot(container);
    root.render(React.createElement(WebVideoPlayer, finalProps));

    // Store instance for cleanup
    activeInstances.set(instanceId, root);

    return instanceId;
  },

  /**
   * Destroy a WebVideoPlayer instance
   * @param instanceId - Instance ID returned from launch, or selector string
   * @returns Promise<boolean> - Returns true if successfully destroyed
   */
  destroy: async (instanceId?: string): Promise<boolean> => {
    try {
      if (instanceId) {
        // If instanceId is provided, find and unmount that specific instance
        const root = activeInstances.get(instanceId);
        if (root) {
          root.unmount();
          activeInstances.delete(instanceId);
          return true;
        }
        return false;
      } else {
        // If no instanceId provided, destroy all instances
        let destroyedCount = 0;
        const instances = Array.from(activeInstances.entries());
        instances.forEach(([id, root]) => {
          root.unmount();
          activeInstances.delete(id);
          destroyedCount++;
        });
        return destroyedCount > 0;
      }
    } catch (error) {
      console.error('Error destroying WebVideoPlayer instance:', error);
      return false;
    }
  },

  /**
   * Get the number of active instances
   * @returns number of active player instances
   */
  getActiveInstanceCount: (): number => {
    return activeInstances.size;
  },
};

// Make it immediately available if DOM is already loaded
if (document.readyState === 'loading') {
  // DOM is still loading
  document.addEventListener('DOMContentLoaded', () => {
    window.WebVideoPlayerDOM = WebVideoPlayerDOM;
  });
} else {
  // DOM is already loaded
  window.WebVideoPlayerDOM = WebVideoPlayerDOM;
}

declare global {
  interface Window {
    WebVideoPlayerDOM: unknown;
  }
}
